"use client";

import { Hammer, Lightbulb, Rocket } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhatHappensNext() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    number: "01",
    title: "ARCHITECT",
    body: "We analyze your current financial profile and credit structure to design a custom blueprint for elite funding and wealth growth.",
    duration: "PHASE ONE",
    Icon: Lightbulb,
    rotation: "rotate-0",
    hoverRotation: "md:hover:scale-[1.02]"
  }, {
    number: "02",
    title: "CONSTRUCT",
    body: "Our team executes the strategy—repairing personal credit, building business profiles, and establishing strategic banking relationships.",
    duration: "PHASE TWO",
    Icon: Hammer,
    rotation: "rotate-0",
    hoverRotation: "md:hover:scale-[1.02]"
  }, {
    number: "03",
    title: "CAPITALIZE",
    body: "With your architecture secured, we facilitate access to high-limit funding and strategic wealth-building opportunities.",
    duration: "PHASE THREE",
    Icon: Rocket,
    rotation: "rotate-0",
    hoverRotation: "md:hover:scale-[1.02]"
  }];
  return <section data-section-id="1124" id="what-happens-next" className="bg-background py-28 md:py-40 relative overflow-hidden" style={{
    borderTop: "1px solid var(--primary)"
  }}>
      <div className="max-w-[1440px] mx-auto px-4 md:px-8">
        {}
        <div ref={headerRef} className={`transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="font-mono text-xs md:text-sm uppercase tracking-[0.2em] text-primary mb-10 md:mb-16" style={{
          letterSpacing: "0.2em"
        }}>
            § 02 / THE BLUEPRINT PROCESS
          </div>

          <h2 className="font-serif text-foreground leading-[1.1] mb-6" style={{
          fontSize: "clamp(2.5rem, 6vw, 5rem)",
          letterSpacing: "-0.02em"
        }}>
            <span>THE PATH TO </span>
            <span className="relative inline-block align-baseline">
              <span className="relative inline-block px-5 md:px-7 py-1 md:py-2" style={{
              backgroundColor: "var(--secondary)",
              color: "var(--foreground)",
              border: "1px solid var(--primary)",
              borderRadius: "0rem",
              transform: "rotate(0deg)",
              boxShadow: "4px 4px 0 var(--primary)"
            }}>
                WEALTH
                {}
                <svg className="absolute -bottom-1 -right-1 w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M2 22 Q 12 12, 22 2 L 22 22 Z" fill="var(--primary)" stroke="var(--primary)" strokeWidth="1" />
                </svg>
              </span>
            </span>
          </h2>

          {}
          <svg className="mb-8 text-primary" width="180" height="14" viewBox="0 0 180 14" fill="none" aria-hidden="true">
            <path d="M0 7 L 180 7" stroke="currentColor" strokeWidth="1" strokeLinecap="square" fill="none" />
          </svg>

          <p className="font-default text-foreground/80 max-w-xl" style={{
          fontSize: "1.25rem",
          lineHeight: "1.6"
        }}>
            A structured methodology designed for serious entrepreneurs. We don't just repair; we engineer your financial future.
          </p>
        </div>

        {}
        <div ref={cardsRef} className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 lg:gap-12 relative">
          {steps.map((step, i) => {
          const Icon = step.Icon;
          const isLast = i === steps.length - 1;
          return <div key={i} data-index={i} className={`relative transition-all duration-700 ease-out ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                <Card className={`group bg-secondary border border-border rounded-md p-7 md:p-8 transform ${step.rotation} ${step.hoverRotation} transition-all duration-300 relative`} style={{
              boxShadow: "var(--shadow-md)",
              transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
            }} onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.boxShadow = "0 0 20px var(--accent)";
              (e.currentTarget as HTMLElement).style.borderColor = "var(--primary)";
            }} onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.boxShadow = "var(--shadow-md)";
              (e.currentTarget as HTMLElement).style.borderColor = "var(--border)";
            }}>
                  {}
                  <span className="absolute top-2 left-2 w-3 h-3" style={{
                borderTop: "1px solid var(--primary)",
                borderLeft: "1px solid var(--primary)"
              }} aria-hidden="true" />
                  <span className="absolute top-2 right-2 w-3 h-3" style={{
                borderTop: "1px solid var(--primary)",
                borderRight: "1px solid var(--primary)"
              }} aria-hidden="true" />
                  <span className="absolute bottom-2 left-2 w-3 h-3" style={{
                borderBottom: "1px solid var(--primary)",
                borderLeft: "1px solid var(--primary)"
              }} aria-hidden="true" />
                  <span className="absolute bottom-2 right-2 w-3 h-3" style={{
                borderBottom: "1px solid var(--primary)",
                borderRight: "1px solid var(--primary)"
              }} aria-hidden="true" />

                  {}
                  <div className="font-serif leading-none text-primary/20" style={{
                fontSize: "clamp(4.5rem, 7vw, 6rem)",
                WebkitTextStroke: "1px var(--primary)"
              }}>
                    {step.number}
                  </div>

                  {}
                  <div className="flex justify-center my-6">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-md" style={{
                  border: "1px solid var(--primary)",
                  backgroundColor: "var(--background)"
                }}>
                      <Icon className="w-10 h-10 text-primary" strokeWidth={1} strokeLinecap="square" strokeLinejoin="miter" />
                    </div>
                  </div>

                  {}
                  <h3 className="font-default font-semibold text-foreground mb-3 uppercase" style={{
                fontSize: "clamp(1.25rem, 2vw, 1.75rem)",
                letterSpacing: "0.05em",
                lineHeight: "1.4"
              }}>
                    {step.title}
                  </h3>

                  {}
                  <p className="font-default text-foreground/75 mb-5" style={{
                fontSize: "1rem",
                lineHeight: "1.6"
              }}>
                    {step.body}
                  </p>

                  {}
                  <div className="font-mono uppercase text-primary font-bold" style={{
                fontSize: "12px",
                letterSpacing: "0.2em"
              }}>
                    {step.duration}
                  </div>
                </Card>

                {}
                {!isLast && <>
                    {}
                    <svg className="hidden md:block absolute top-1/2 -right-6 lg:-right-8 w-12 lg:w-16 h-12 text-primary/40 pointer-events-none" viewBox="0 0 64 48" fill="none" aria-hidden="true" style={{
                transform: "translateY(-50%)"
              }}>
                      <path d="M2 24 L 56 24" stroke="currentColor" strokeWidth="1" strokeLinecap="square" fill="none" />
                      <path d="M50 18 L 56 24 L 50 30" stroke="currentColor" strokeWidth="1" strokeLinecap="square" fill="none" />
                    </svg>

                    {}
                    <svg className="md:hidden mx-auto mt-6 w-12 h-14 text-primary/40" viewBox="0 0 48 64" fill="none" aria-hidden="true">
                      <path d="M24 2 L 24 56" stroke="currentColor" strokeWidth="1" strokeLinecap="square" fill="none" />
                      <path d="M18 50 L 24 56 L 30 50" stroke="currentColor" strokeWidth="1" strokeLinecap="square" fill="none" />
                    </svg>
                  </>}
              </div>;
        })}
        </div>
      </div>
    </section>;
}